/**
 * The change routes the console reads (ADR-218), typed from
 * specs/changes/change-page/openapi.yaml; the types are lib/changes/model's.
 * The console writes nothing here: a state is read from GitHub at sync time,
 * and the owner approves where they always did, by merging.
 */
import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';
import type { Change, ChangeList, ChangeSummary } from '@/lib/changes/model';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export interface ChangeListParams {
  repo?: string;
  waiting?: boolean;
  /** Include the archived changes, marked done. */
  archived?: boolean;
  limit?: number;
  offset?: number;
}

/** The most `GET /changes` answers at once (openapi.yaml). */
const PAGE = 100;

export const changesApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  async listChanges(params?: ChangeListParams): Promise<ChangeList> {
    const { data } = await api.get('/changes', { params });
    return data;
  },

  /** Every change the caller may read, page by page. */
  async listAllChanges(params?: Omit<ChangeListParams, 'limit' | 'offset'>): Promise<ChangeSummary[]> {
    const items: ChangeSummary[] = [];
    for (;;) {
      const page = await changesApi.listChanges({ ...params, limit: PAGE, offset: items.length });
      items.push(...page.items);
      if (page.items.length === 0 || items.length >= page.total) return items;
    }
  },

  async getChange(owner: string, repo: string, slug: string): Promise<Change> {
    const { data } = await api.get(`/changes/${owner}/${repo}/${slug}`);
    return data;
  },
};
